/* eslint-disable @typescript-eslint/no-explicit-any */
import { CSSResultGroup, html, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { SlotsNGController as SlotController } from '../../controllers/slots-ng-controller';
import { ResizeController } from '../../controllers/resize-controller';
import { screenSizeMap } from '../../controllers/screen-size-controller';
import componentStyles from './sic-video-player.css.lit';
import defaultVars from './css-variables/vars-default.css.lit';

export type MediaOrientation = 'full' | 'left' | 'right';

/**
 * @slot Headline
 * @slot description
 * @slot video iframe
 * @slot CTA
 * @prop {String}: background (none, primary-lightest)
 * @prop {String}: media-orientation (full, left, right)
 */

@customElement('sic-video-player')
export class SicVideoPlayer extends OutlineElement {
  slotsController = new SlotController(this);
  resizeController: ResizeController;
  static styles: CSSResultGroup = [defaultVars, componentStyles];

  // The resizeController is initialized here because visibleBreakpoint is not set yet when constructor runs
  connectedCallback() {
    super.connectedCallback();
    this.resizeController = new ResizeController(this, {
      breakpoints: [screenSizeMap.get(this.visibleBreakpoint) as number],
      containerQuery: false,
    });
  }

  @property({ type: String, attribute: 'media-orientation' })
  mediaOrientation: MediaOrientation = 'full';

  /**
   * The breakpoint at which the switch from mobile to desktop layout.
   *
   * This is done as a variable to ensure flexibility for future usages.
   */
  @property({ type: String, attribute: 'breakpoint' })
  visibleBreakpoint = 'lg';

  @state() private isStacked = false;

  render(): TemplateResult {
    this.isStacked = this.resizeController.currentBreakpointRange === 0;

    if (this.mediaOrientation === 'full') {
      return html`
        <div class="container">
          <div class="content">
            <sic-heading level-size="6" class="headline-06">
              ${this.slotsController.conditionalSlot('headline')}
            </sic-heading>
            ${this.slotsController.conditionalSlot('description', false)}
            ${this.slotsController.conditionalSlot('cta', false)}
          </div>
          <div class="video-wrapper">
            ${this.slotsController.conditionalSlot('video', false)}
          </div>
        </div>
      `;
    } else if (this.mediaOrientation === 'right' || this.isStacked) {
      return html`
        <div class="container--side-by-side container">
          <div class="content">
            <sic-heading level-size="6">
              ${this.slotsController.conditionalSlot('headline')}
            </sic-heading>
            ${this.slotsController.conditionalSlot('description', false)}
            ${this.slotsController.conditionalSlot('cta', false)}
          </div>
          <div class="video-wrapper">
            ${this.slotsController.conditionalSlot('video', false)}
          </div>
        </div>
      `;
    } else {
      return html`
        <div class="container--side-by-side container">
          <div class="video-wrapper">
            ${this.slotsController.conditionalSlot('video', false)}
          </div>
          <div class="content">
            <sic-heading level-size="6">
              ${this.slotsController.conditionalSlot('headline')}
            </sic-heading>
            ${this.slotsController.conditionalSlot('description', false)}
            ${this.slotsController.conditionalSlot('cta', false)}
          </div>
        </div>
      `;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sic-video-player': SicVideoPlayer;
  }
}
